import { createFileRoute } from '@tanstack/react-router'


import {
    getGridSingleSelectOperators,
    GridColDef,
    GridComparatorFn,
    GridRenderCellParams
} from '@mui/x-data-grid-premium'

import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import { getOrganization } from '@/store/auth/authSlice'
import i18next, { t } from 'i18next'
import { useSelector } from 'react-redux'

import { awardSchema, AwardType, defaultAward } from '@/api/awards'
import {
    organizationAwardsQueryOptions,
    useCreateAwardMutation,
    useDeleteAwardMutation,
    useUpdateAwardMutation
} from '@/api/awardsQueryOptions'
import { OrganizationLevelType } from '@/api/organizations'
import { SectionType } from '@/api/sections'
import { sectionsAvailableQueryOptions } from '@/api/sectionsQueryOptions'
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord'
import AbbreviationColumn from '@/components/grid/AbbreviationColumn'
import DescriptionColumn from '@/components/grid/DescriptionColumn'
import NameColumn from '@/components/grid/NameColumn'
import useOrganizationDetailView from '@/components/hooks/useOrganizationDetailView'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import SectionEditCell from '@/components/mui-x/SectionEditCell'
import SelectOrganizationLevel from '@/components/select/SelectOrganizationType'
import { yupResolver } from '@hookform/resolvers/yup'
import { Autocomplete, Grid } from '@mui/material'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Controller } from 'react-hook-form'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/awards',
)({
    component: OrganizationAwardsComponent,
})

function OrganizationAwardsComponent() {
    const params = Route.useParams()

    const { query, data, createable, updateMutation, deleteMutation, onSubmit } = useOrganizationDetailView<AwardType>(params, organizationAwardsQueryOptions,
        'Award', useCreateAwardMutation, useUpdateAwardMutation, useDeleteAwardMutation)

    const sessionOrganization = useSelector(getOrganization)

    const sectionsQuery = useSuspenseQuery(sectionsAvailableQueryOptions())
    const sections = sectionsQuery.data;

    const { large, xlarge } = useResponsive()

    const columns: GridColDef[] = []

    columns.push(
        AbbreviationColumn(10),
        NameColumn(100),
    )

    const organizationLevels = [] as OrganizationLevelType[]
    if (sessionOrganization.organizationLevel === OrganizationLevelType.TROOP) {
        organizationLevels.push(OrganizationLevelType.SECTION, OrganizationLevelType.TROOP)
    } else if (sessionOrganization.organizationLevel === OrganizationLevelType.DIVISION) {
        organizationLevels.push(OrganizationLevelType.SECTION, OrganizationLevelType.TROOP, OrganizationLevelType.DIVISION)
    } else if (sessionOrganization.organizationLevel === OrganizationLevelType.FEDERATION) {
        organizationLevels.push(OrganizationLevelType.SECTION, OrganizationLevelType.TROOP, OrganizationLevelType.DIVISION, OrganizationLevelType.FEDERATION)
    }

    if (large || xlarge) {
        columns.push({
            field: 'organizationLevel',
            type: 'singleSelect',
            valueOptions: organizationLevels.map((level) => ({
                value: level,
                label: t(`enum.organizationLevel.${level}`),
            })),
            valueFormatter: (_params, row) => {
                if (row.organizationLevel) {
                    return t(`enum.organizationLevel.${row.organizationLevel}`)
                }
                return ''
            },
            minWidth: 100,
            headerName: t('property.award.organizationLevel'),
            editable: true,
        })

        const isAnyOfOperator = getGridSingleSelectOperators().filter((operator) => operator.value === 'isAnyOf')[0]
        const enhancedIsAnyOfOperator = {
            ...isAnyOfOperator,
            getApplyFilterFn: (filterItem: any) => {
                if (!filterItem.field || !filterItem.value || !filterItem.operator) {
                    return null;
                }

                return (value: any) => {
                    if (Array.isArray(filterItem.value) && Array.isArray(value)) {
                        return filterItem.value.some((filterValue: any) => value.map((v) => v.uuid).includes(filterValue))
                    }
                    if (Array.isArray(filterItem.value) && filterItem.value.length == 0) {
                        return true
                    }
                    return false;
                };
            },
        }

        const sortComparator: GridComparatorFn = (v1, v2) => {
            const s1 = v1 ? v1.map((o: any) => o.abbreviation).join(', ') : ''
            const s2 = v2 ? v2.map((o: any) => o.abbreviation).join(', ') : ''
            return s1.localeCompare(s2)
        }

        columns.push(
            {
                field: 'sections',
                headerName: i18next.t('property.award.sections'),
                valueFormatter: (_params, row) => {
                    if (row.sections) {
                        return row.sections
                            .map((section: SectionType) => section.abbreviation)
                            .join(', ')
                    }
                    return ''
                },
                renderEditCell: (params: GridRenderCellParams) => {
                    return <SectionEditCell {...params} parent />
                },
                editable: true,
                type: 'singleSelect',
                valueOptions: sections,
                getOptionValue: (option: any) => option.uuid,
                getOptionLabel: (option: any) => option.name,
                filterOperators: [enhancedIsAnyOfOperator],
                sortComparator: sortComparator,
            }
        )

        columns.push(
            DescriptionColumn(),
        )
    }

    const createDetail = useCreateDetail<AwardType>({
        onSubmit,
        resolver: yupResolver<AwardType, any, any>(awardSchema),
        defaultValues: {
            ...defaultAward,
            organization: {
                uuid: params.organizationUuid,
            },
        },
        title: t('action.new', { entity: t('entity.award') }),
    })

    const editGrid = useEditGrid<AwardType>({
        defaultValues: defaultAward,
        updateMutation: updateMutation,
        deleteMutation: deleteMutation,
        records: data,
        columns: columns,
        title: t('entity.award'),
    })

    if (query.isLoading || query.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            <Typography>{t('entity.award_other')}</Typography>
            {createable && (
                <CreateDetailAccordion {...createDetail}>
                    <Grid container spacing={2}>
                        <input
                            type="hidden"
                            defaultValue={params.organizationUuid}
                            {...createDetail.form.register('organization.uuid')}
                        />
                        <input
                            type="hidden"
                            defaultValue={sessionOrganization.organizationLevel}
                            {...createDetail.form.register('organizationLevel')}
                        />
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4,
                            }}
                        >
                            <TextField
                                defaultValue={defaultAward.name}
                                required
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                {...createDetail.form.register('name')}
                                label={i18next.t('property.general.name')}
                                helperText={createDetail.form.formState.errors.name?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 4,
                                lg: 2,
                            }}
                        >
                            <TextField
                                defaultValue={defaultAward.abbreviation}
                                required
                                slotProps={{ htmlInput: { maxLength: 10 } }}
                                {...createDetail.form.register('abbreviation')}
                                label={i18next.t('property.general.abbreviation')}
                                helperText={createDetail.form.formState.errors.abbreviation?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectOrganizationLevel control={createDetail.form.control} name="organizationLevel" limitDown={true} includeSection={true} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <Controller
                                control={createDetail.form.control}
                                name='sections'
                                defaultValue={[]}
                                render={({ field }) => {
                                    return (
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            getOptionKey={(option) => option.uuid || ''}
                                            getOptionLabel={(option) => option.abbreviation || ''}
                                            filterSelectedOptions
                                            onChange={(_event, item) => {
                                                field.onChange(item)
                                            }}
                                            options={sections}
                                            value={field.value}
                                            size="small"
                                            style={{ width: '100%', marginTop: 0 }}
                                            renderInput={(params) => (
                                                <TextField {...params} size="small" label={t('property.award.sections')} />
                                            )}
                                        >
                                        </Autocomplete>
                                    );
                                }} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={defaultAward.description}
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                {...createDetail.form.register('description')}
                                label={i18next.t('property.general.description')}
                                helperText={createDetail.form.formState.errors.description?.message}
                            />
                        </Grid>
                    </Grid>
                </CreateDetailAccordion>
            )}
            <EditGrid {...editGrid}></EditGrid>
        </>
    )
}
